import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';
import { useCart } from '@/context/CartContext';
import { MEETING_BOOTH_LARGE_ACCESSORIES, MEETING_BOOTH_ACCESSORIES } from '@/components/product/ProductConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Upsell strategy mapping
const UPSELL_CATEGORIES = {
  'Modular Office Workstation': ['Storage Cabinets', 'Privacy Booths'],
  'Storage Cabinets': ['Modular Office Workstation', 'Privacy Booths'],
  'Privacy Booths': ['Modular Office Workstation', 'Storage Cabinets']
};

// Booth accessories based on subcategory - ONLY for Meeting Booth Large (HushPod Quad)
const BOOTH_ACCESSORIES = {
  'Meeting Booth Large': [
    { ...MEETING_BOOTH_LARGE_ACCESSORIES.seatingBench, category: 'Accessory', subcategory: 'Seating Bench' },
    { ...MEETING_BOOTH_LARGE_ACCESSORIES.table, category: 'Accessory', subcategory: 'Table' }
  ]
};

export default function CartUpsell({ cartItems }) {
  const [recommendations, setRecommendations] = useState([]);
  const [boothAccessories, setBoothAccessories] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    if (!cartItems || cartItems.length === 0) return;
    
    const fetchRecommendations = async () => {
      try {
        // Get categories and subcategories in cart
        const cartCategories = [...new Set(cartItems.map(item => item.category))];
        const cartSubcategories = [...new Set(cartItems.map(item => item.subcategory).filter(Boolean))];
        const cartProductIds = cartItems.map(item => item.id);
        
        // Check for booth accessories
        const accessories = [];
        cartSubcategories.forEach(subcat => {
          if (BOOTH_ACCESSORIES[subcat]) {
            BOOTH_ACCESSORIES[subcat].forEach(acc => {
              // Don't add if already in cart
              if (!cartProductIds.includes(acc.id)) {
                accessories.push(acc);
              }
            });
          }
        });
        setBoothAccessories(accessories);
        
        // Get target categories for upsell (only if no booth accessories)
        if (accessories.length === 0) {
          const targetCategories = new Set();
          cartCategories.forEach(cat => {
            const targets = UPSELL_CATEGORIES[cat] || [];
            targets.forEach(t => targetCategories.add(t));
          });

          if (targetCategories.size === 0) return;

          const response = await axios.get(`${API_URL}/products`);
          const allProducts = response.data.products || response.data || [];
          
          // Get products from recommended categories (not already in cart)
          const recommended = allProducts
            .filter(p => targetCategories.has(p.category) && !cartProductIds.includes(p.id))
            .slice(0, 3);
          
          setRecommendations(recommended);
        } else {
          setRecommendations([]);
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      }
    };

    fetchRecommendations();
  }, [cartItems]);

  const handleQuickAdd = (product) => {
    addToCart(product, 1);
  };

  // Show booth accessories if available, otherwise show regular recommendations
  const itemsToShow = boothAccessories.length > 0 ? boothAccessories : recommendations;
  
  if (itemsToShow.length === 0) return null;

  return (
    <div className="mt-8 border-2 border-yellow-400 bg-yellow-50 p-6">
      <h3 className="text-lg font-black uppercase mb-4">
        {boothAccessories.length > 0 ? 'Recommended Accessories' : 'Complete Your Order'}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {itemsToShow.map((product) => (
          <div key={product.id} className="flex items-center gap-3 bg-white p-3 border">
            {product.image_url && (
              <img
                src={product.image_url || product.image_urls?.[0]}
                alt={product.name}
                className="w-16 h-16 object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              {product.category === 'Accessory' ? (
                <p className="font-bold text-sm truncate">{product.name}</p>
              ) : (
                <Link to={`/products/${product.id}`} className="hover:underline">
                  <p className="font-bold text-sm truncate">{product.name}</p>
                </Link>
              )}
              <p className="text-xs text-gray-500">{product.subcategory || product.category}</p>
              {product.mrp && product.mrp > product.price && (
                <p className="text-xs text-gray-400 line-through">{formatPrice(product.mrp)}</p>
              )}
              <p className="font-black">{formatPrice(product.price)}</p>
            </div>
            <Button 
              size="sm" 
              onClick={() => handleQuickAdd(product)}
              className="bg-yellow-500 hover:bg-yellow-600 rounded-none"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
